import { type IExistContact, regexEmail, useCreateContact, useGetExistsContact } from "@api";
import type { IContact } from "@bbj-types/contact";
import { ContactType } from "@bbj-types/contact/enum";
import { useGlobalContext } from "@root/context";
import { useCallback, useState } from "react";
import type { ContactPickerEnterprise, ContactPickerMode } from "./types";

export interface ContactFormError {
  contactInformation: boolean;
  place: boolean;
}

const errorInitialState: ContactFormError = {
  contactInformation: false,
  place: false,
};

export const getContactOptionLabel = (contact: IContact) => {
  if (contact.firstName && contact.lastName) {
    return `${contact.firstName} ${contact.lastName}`;
  }
  if (contact.firstName) {
    return contact.firstName;
  }
  if (contact.lastName) {
    return contact.lastName;
  }
  if (contact.email) {
    return contact.email;
  }
  if (contact.phone) {
    return contact.phone;
  }
  return "Inconnu";
};

export interface UseContactPickerArgs {
  type: ContactType;
  enterprise: ContactPickerEnterprise | null;
  value: IContact | null;
  onChange: (contact: IContact | null) => void;
  isSharedEnterprise: boolean;
}

export const useContactPicker = ({
  type,
  enterprise,
  value: contextContact,
  onChange: setContextContact,
  isSharedEnterprise,
}: UseContactPickerArgs) => {
  const { user } = useGlobalContext();
  const [{ loading: loadingExists }, getExistsContact] = useGetExistsContact();
  const [{ loading: loadingCreate }, createContact] = useCreateContact();
  const loading = loadingExists || loadingCreate;
  const [expanded, setExpanded] = useState(false);
  const [contact, setContact] = useState<IContact | null>({
    _id: "",
    firstName: "",
    lastName: "",
    job: "",
    email: "",
    phone: "",
    description: "",
    organisation: "",
    isTemp: isSharedEnterprise,
    place: user.organisation.places,
    type,
    createdAt: "",
    updatedAt: "",
  });
  const [mode, setMode] = useState<ContactPickerMode>("");
  const [error, setError] = useState<ContactFormError>(errorInitialState);
  const [alreadyExistsContact, setAlreadyExistsContact] = useState<IExistContact>({
    contact: null,
    enterpriseId: null,
  });

  const changeMode = useCallback(
    (mode: string) => {
      switch (mode) {
        case "create":
          setMode(mode);
          if (contextContact) {
            setContact(contextContact);
            setContextContact(null);
          } else {
            setContact({
              _id: "",
              firstName: "",
              lastName: "",
              job: "",
              email: "",
              phone: "",
              description: "",
              organisation: "",
              isTemp: isSharedEnterprise,
              place: user.organisation.places,
              type,
              createdAt: "",
              updatedAt: "",
            });
          }
          break;
        case "select":
          setMode(mode);
          setContact(contextContact);
          break;
        case "":
          setMode(mode);
          break;
      }
    },
    [contextContact, setContextContact, isSharedEnterprise, type, user.organisation.places]
  );

  const handleReset = useCallback(() => {
    setExpanded(false);
    setContact(null);
    setAlreadyExistsContact({ contact: null, enterpriseId: null });
    setError(errorInitialState);
  }, []);

  const handleError = useCallback((contact: IContact | null) => {
    if (contact) {
      if (!contact.email && !contact.phone) {
        setError((error) => ({ ...error, contactInformation: true }));
      } else if (contact.email || contact.phone) {
        setError((error) => ({ ...error, contactInformation: false }));
      }
      if (contact.place._id === "") {
        setError((error) => ({ ...error, place: true }));
      } else if (contact.place._id !== "") {
        setError((error) => ({ ...error, place: false }));
      }
    }
  }, []);

  const handleSubmit = useCallback(
    async (optContact?: IContact) => {
      const nContact = optContact || contact;
      if (nContact?._id) {
        setContextContact(nContact);
        handleReset();
      } else if ((nContact?.email || nContact?.phone) && nContact?.place._id !== "") {
        if (nContact.email && !nContact.email.match(regexEmail)) {
          setError((error) => ({ ...error, contactInformation: true }));
          return false;
        }
        try {
          const response = await getExistsContact(nContact.email, nContact.phone);
          if (response.status === 204) {
            if (type !== ContactType.ENTERPRISE || !enterprise?._id) {
              setContextContact(nContact);
              handleReset();
              return false;
            }
            const createResponse = await createContact({
              firstName: nContact.firstName,
              lastName: nContact.lastName,
              job: nContact.job,
              email: nContact.email,
              phone: nContact.phone,
              description: nContact.description,
              isTemp: nContact.isTemp,
              type: ContactType.ENTERPRISE,
              enterprise: enterprise._id,
              place: nContact.place,
            });
            if (createResponse.status === 201) {
              const { author: _author, ...createdContact } = createResponse.data;
              setContextContact(createdContact as IContact);
              handleReset();
            }
          } else if (response.data) {
            setAlreadyExistsContact(response.data);
          }
        } catch (err) {
          console.error(err);
          handleError(nContact);
        }
      } else {
        handleError(nContact);
      }
      return false;
    },
    [contact, setContextContact, getExistsContact, createContact, type, enterprise, handleReset, handleError]
  );

  const handleCancel = useCallback(() => {
    setContextContact(null);
    changeMode("");
    handleReset();
    return true;
  }, [handleReset, setContextContact, changeMode]);

  const contactsNumber = enterprise?.contacts?.length || 0;

  return {
    expanded,
    setExpanded,
    loading,
    enterprise,
    contactsNumber,
    contact,
    contextContact,
    alreadyExistsContact,
    mode,
    handleSubmit,
    handleCancel,
    setContact,
    changeMode,
    error,
    isSharedEnterprise,
  };
};
